import React, { useState } from 'react';
import type { Column } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { toDateString } from '@/lib/utils';
import { Popover, PopoverTrigger, PopoverContent } from '@/global/base/popover';
import { Calendar } from '@/global/base/calendar.tsx';
import { Button } from '@/global/base/button';

type DateRangeColumnFilterProps<TData> = {
    column: Column<TData, unknown>;
};

type CalendarState = {
    fromCalendarOpen: boolean;
    toCalendarOpen: boolean;
    month: Date;
    isInvalid: boolean;
};

type DateRangeValue = {
    from?: string;
    to?: string;
};

const DateRangeColumnFilter = <TData,>({ column }: DateRangeColumnFilterProps<TData>) => {
    const [calendarState, setCalendarState] = useState<CalendarState>({
        fromCalendarOpen: false,
        toCalendarOpen: false,
        month: new Date(),
        isInvalid: false
    });

    const filterValue = column.getFilterValue() as DateRangeValue | undefined;

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const currentRange = filterValue || {};

        if (inputValue) {
            const parsedDate = parse(inputValue, 'yyyy-MM-dd', new Date());
            if (isValid(parsedDate)) {
                column.setFilterValue({
                    ...currentRange,
                    from: toDateString(parsedDate)
                });
                setCalendarState((previous) => ({ ...previous, month: parsedDate, isInvalid: false }));
            } else {
                setCalendarState((previous) => ({ ...previous, isInvalid: true }));
            }
        } else {
            column.setFilterValue({
                ...currentRange,
                from: undefined
            });
            setCalendarState((previous) => ({ ...previous, isInvalid: false }));
        }
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const currentRange = filterValue || {};

        if (inputValue) {
            const parsedDate = parse(inputValue, 'yyyy-MM-dd', new Date());
            if (isValid(parsedDate)) {
                column.setFilterValue({
                    ...currentRange,
                    to: toDateString(parsedDate)
                });
                setCalendarState((previous) => ({ ...previous, month: parsedDate, isInvalid: false }));
            } else {
                setCalendarState((previous) => ({ ...previous, isInvalid: true }));
            }
        } else {
            column.setFilterValue({
                ...currentRange,
                to: undefined
            });
            setCalendarState((previous) => ({ ...previous, isInvalid: false }));
        }
    };

    const handleFromCalendarSelect = (date: Date | undefined) => {
        const currentRange = filterValue || {};
        column.setFilterValue({
            ...currentRange,
            from: date ? toDateString(date) : undefined
        });
        setCalendarState((previous) => ({
            ...previous,
            fromCalendarOpen: false,
            isInvalid: false,
            month: date || new Date()
        }));
    };

    const handleToCalendarSelect = (date: Date | undefined) => {
        const currentRange = filterValue || {};
        column.setFilterValue({
            ...currentRange,
            to: date ? toDateString(date) : undefined
        });
        setCalendarState((previous) => ({
            ...previous,
            toCalendarOpen: false,
            isInvalid: false,
            month: date || new Date()
        }));
    };

    const formatDateForInput = (dateString: string | undefined) => {
        if (!dateString) return '';

        return dateString; // Already in 'yyyy-MM-dd' format from toDateString()
    };

    const formatDateForDisplay = (dateString: string | undefined) => {
        if (!dateString) return '';
        try {
            // Use parse for date-only strings to avoid timezone issues
            const date = parse(dateString, 'yyyy-MM-dd', new Date());

            return format(date, 'MMM dd, yyyy');
        } catch {
            return '';
        }
    };

    const hasValue = filterValue?.from || filterValue?.to;

    return (
        <div className="space-y-2">
            {/* From Date Input */}
            <div className="space-y-1">
                <label className="text-xs text-gray-600">From:</label>
                <div className="flex items-center space-x-2">
                    <input
                        type="date"
                        className={`h-8 flex-grow border rounded px-2 py-1 text-sm ${
                            calendarState.isInvalid ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={formatDateForInput(filterValue?.from)}
                        onChange={handleFromDateChange}
                        placeholder="From date..."
                    />

                    {/* From Calendar Button */}
                    <Popover
                        open={calendarState.fromCalendarOpen}
                        onOpenChange={(open) => setCalendarState((previous) => ({ ...previous, fromCalendarOpen: open }))}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCalendarState((previous) => ({ ...previous, fromCalendarOpen: !previous.fromCalendarOpen }))}
                            >
                                <CalendarIcon className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={filterValue?.from ? parse(filterValue.from, 'yyyy-MM-dd', new Date()) : undefined}
                                onSelect={handleFromCalendarSelect}
                                month={calendarState.month}
                                onMonthChange={(month) => setCalendarState((previous) => ({ ...previous, month }))}
                                disabled={(date) => date < new Date('1900-01-01')}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* To Date Input */}
            <div className="space-y-1">
                <label className="text-xs text-gray-600">To:</label>
                <div className="flex items-center space-x-2">
                    <input
                        type="date"
                        className={`h-8 flex-grow border rounded px-2 py-1 text-sm ${
                            calendarState.isInvalid ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={formatDateForInput(filterValue?.to)}
                        onChange={handleToDateChange}
                        placeholder="To date..."
                    />

                    {/* To Calendar Button */}
                    <Popover
                        open={calendarState.toCalendarOpen}
                        onOpenChange={(open) => setCalendarState((previous) => ({ ...previous, toCalendarOpen: open }))}
                    >
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setCalendarState((previous) => ({ ...previous, toCalendarOpen: !previous.toCalendarOpen }))}
                            >
                                <CalendarIcon className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                            <Calendar
                                mode="single"
                                selected={filterValue?.to ? parse(filterValue.to, 'yyyy-MM-dd', new Date()) : undefined}
                                onSelect={handleToCalendarSelect}
                                month={calendarState.month}
                                onMonthChange={(month) => setCalendarState((previous) => ({ ...previous, month }))}
                                disabled={(date) => date < new Date('1900-01-01')}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Current range display */}
            <div className="flex items-center justify-between">
                {/* Display current range */}
                {hasValue &&
                    <div className="text-xs text-gray-500">
                        {filterValue?.from && formatDateForDisplay(filterValue.from)}
                        {filterValue?.from && filterValue?.to && ' - '}
                        {filterValue?.to && formatDateForDisplay(filterValue.to)}
                    </div>
                }
            </div>
        </div>
    );
};

export default DateRangeColumnFilter;
